import { type FC, useCallback } from "react";
import type { PartnerData } from "src/http/api-types";
import { useContent } from "src/shared/components/content";
import { SECONDARY_COLOR_CAT_IDS } from "../../../constants";
import {
  EventDetailContainer,
  MatchupContainer,
  StyledContentContainer,
  StyledEventDetailText,
  StyledEventTitle,
  StyledImage,
  StyledMatchupCard,
  StyledNameWrapper,
  StyledTeamName,
  StyledVsDiv,
  TeamContainer,
} from "./styled-components";

const defaultPartnerData = [] as const;

interface Props {
  className?: string;
  dayOfWeek?: string | null;
  eventId?: number;
  isTimeConfirmed?: boolean | null;
  localEventDate?: string | null;
  localEventTime?: string | null;
  eventName?: string | null;
  onClick?: ((id: number | undefined) => void) | undefined;
  partnerData?: readonly PartnerData[] | null;
  url?: string | null;
  venueName?: string | null;
}

/**
 * Matchup Card UI for Sports Team Events Near You
 * @see https://www.figma.com/file/Y9JIAqbF7ljecUFj4yAypS/General-Innovation-XPs---Master?type=design&node-id=308-3991&mode=dev
 */
export const MatchupCard: FC<Props> = ({
  className,
  dayOfWeek,
  eventId,
  isTimeConfirmed,
  localEventDate,
  localEventTime,
  eventName,
  onClick,
  partnerData = defaultPartnerData,
  url,
  venueName,
}) => {
  const tbaContent = useContent("cid$#3860643#"); // "TBA"
  const dateTimeText = localEventDate
    ? isTimeConfirmed === false
      ? `${dayOfWeek} ${localEventDate} • ${tbaContent}`
      : `${dayOfWeek} ${localEventDate} • ${localEventTime}`
    : tbaContent;
  const handleOnClick = useCallback(() => {
    onClick?.(eventId);
  }, [eventId, onClick]);

  return (
    <StyledMatchupCard className={className} onClick={handleOnClick} url={url ?? "#"}>
      <StyledContentContainer>
        <MatchupContainer>
          <StyledVsDiv>vs</StyledVsDiv>
          {partnerData?.map((team, idx) => {
            const { id, partnerLogoUrl, primaryColor, secondaryColor } = team;
            // Some teams primary colors are incorrect and/or not presentationally effective - Use secondary in these scenarios
            const bgColor = id && SECONDARY_COLOR_CAT_IDS.has(id) ? secondaryColor : primaryColor;

            return (
              <TeamContainer key={team.id} bgColor={bgColor ?? undefined} left={idx === 0} right={idx === 1}>
                {partnerLogoUrl ? (
                  <StyledImage alt="" size={{ height: 140, width: 140 }} src={partnerLogoUrl} />
                ) : (
                  <StyledNameWrapper>
                    <StyledTeamName>{team.name}</StyledTeamName>
                  </StyledNameWrapper>
                )}
              </TeamContainer>
            );
          })}
        </MatchupContainer>
        <EventDetailContainer>
          <StyledEventTitle>{eventName}</StyledEventTitle>
          <StyledEventDetailText>{dateTimeText}</StyledEventDetailText>
          <StyledEventDetailText>{venueName}</StyledEventDetailText>
        </EventDetailContainer>
      </StyledContentContainer>
    </StyledMatchupCard>
  );
};
